import downloadIcon from 'assets/img/download-icon.svg';
import cn from 'classnames';
import Button from 'components/Button';
import Select from 'components/Select';
import { i18nPath } from 'config/i18nNs';
import { Col, Row } from 'layout/Grid';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  parseMarketsListToOptions,
  translateLangName
} from 'utils/countriesAndLangs';
import { getPathInfo } from 'utils/versionUtils';
import styles from './documentsSelector.module.scss';

const DocumentsSelector = () => {
  const { lang } = getPathInfo();
  const isRTL = lang === 'ug';

  const [selectedMarket, setSelectedMarket] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [languagesList, setLanguagesList] = useState([]);
  const [entitesList, setEntitiesList] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [pdfUrl, setPdfUrl] = useState('');
  const { t } = useTranslation(['dictionary', 'speakUp']);

  const marketsList = t(`${i18nPath.speakUp}`, { files: [] })?.files || [];

  const onMarketUpdate = (item: {
    label: string;
    value: {
      code: string;
      flag: string;
      files: { file: { url: string }; languageCode: string }[];
    };
  }) => {
    setEntitiesList([]);
    const { files } = item?.value;
    const parsedLanguagesList = files.map(
      (file: { file: { url: string }; languageCode: string }) => {
        const langName = translateLangName(file?.languageCode, lang);
        return { label: langName, value: file?.languageCode };
      }
    );

    setSelectedMarket(item);
    setLanguagesList(parsedLanguagesList);
    setSelectedLanguage(null);
  };

  const onLangUpdate = (item: { value: string; label: string }) => {
    setEntitiesList([]);

    const { value: code } = item;
    const { countryCode: currentMarket } = selectedMarket?.value;

    const marketInfo = marketsList.find(
      (marketItem: { countryCode: string }) => {
        return marketItem.countryCode === currentMarket;
      }
    );

    const languageInfo = marketInfo?.files?.find(
      (langItem: { languageCode: string }) => langItem.languageCode === code
    );

    if (!languageInfo) {
      console.warn('No language info found or pdf url for', code);
    }

    const { languageCode, contentType, entities } = languageInfo || {};

    const langName = translateLangName(languageCode, lang);

    // Poland exception, there is more than one entity per language,
    // display entities dropdown
    if (contentType === 'speakUpElementLanguageMultipleEntities') {
      const parsedEntitiesList = entities?.map(
        (entity: { file: string; label: string }) => ({
          label: entity?.label,
          value: entity?.label
        })
      );
      setEntitiesList(parsedEntitiesList);
      setSelectedLanguage({ label: langName, value: languageCode });
      return;
    }

    const { url: pdfFileUrl } = languageInfo?.file?.[0];
    setEntitiesList([]);
    setPdfUrl(pdfFileUrl);
    setSelectedLanguage({ label: langName, value: languageCode });
  };

  const onEntityUpdate = (item: { value: string; label: string }) => {
    const { value: entity } = item;
    const { countryCode: currentMarket } = selectedMarket?.value;
    const marketInfo = marketsList.find(
      (marketItem: { countryCode: string }) => {
        return marketItem.countryCode === currentMarket;
      }
    );

    const languageInfo = marketInfo?.files?.find(
      (langItem: { languageCode: string }) =>
        langItem.languageCode === selectedLanguage?.value
    );

    const entityInfo = languageInfo?.entities.find(
      (entityItem: { label: string; file: string }) =>
        entityItem.label === entity
    );

    const { url: pdfFileUrl } = entityInfo?.file?.[0];
    setPdfUrl(pdfFileUrl);
    setSelectedEntity(item);
  };

  const onDownloadPdf = () => {
    window.open(pdfUrl, '_blank');
  };

  return (
    <section className={cn(styles.root, { [styles.rtl]: isRTL })}>
      <h3 className={styles.title}>
        {t(`${i18nPath.dictionary}.downloadManual`)}
      </h3>

      <Row>
        <Col sm={12} md={6}>
          <div className={styles.topMargin}>
            <label className={styles.label} htmlFor="select-country">
              {t(
                `${i18nPath.dictionary}.selectYourCountry`,
                'select your country'
              )}
            </label>
            <Select
              options={parseMarketsListToOptions(marketsList, lang)}
              value={selectedMarket}
              onChange={onMarketUpdate}
              placeholder={t(`${i18nPath.dictionary}.selectYourCountry`)}
              displayFlags
              id="country-selector"
            />
          </div>
        </Col>
      </Row>
      <Row className={styles.downloadRow}>
        <Col sm={12} md={6}>
          <Row>
            <Col sm={12}>
              <label className={styles.label} htmlFor="select-language">
                {t(
                  `${i18nPath.dictionary}.selectLanguagePlaceholder`,
                  'select your language'
                )}
              </label>

              <Select
                options={languagesList}
                value={selectedLanguage}
                onChange={onLangUpdate}
                placeholder={t(
                  `${i18nPath.dictionary}.selectLanguagePlaceholder`
                )}
                disabled={!selectedMarket}
                id="language-selector"
              />
            </Col>
          </Row>
        </Col>
      </Row>

      {entitesList?.length > 0 && (
        <Row className={styles.downloadRow}>
          <Col sm={12} md={6}>
            <Row>
              <Col sm={12}>
                <label className={styles.label} htmlFor="select-language">
                  {t(`${i18nPath.dictionary}.selectEntityPlaceholder`)}
                </label>
                <Select
                  options={entitesList}
                  value={selectedEntity}
                  onChange={onEntityUpdate}
                  placeholder={t(
                    `${i18nPath.dictionary}.selectEntityPlaceholder`
                  )}
                  id="entity-selector"
                />
              </Col>
            </Row>
          </Col>
        </Row>
      )}

      <Row className={styles.buttonRow}>
        <Col sm={12} md={6}>
          <Button
            variant="primary"
            fullWidth
            onClick={onDownloadPdf}
            disabled={!pdfUrl}
            icon={downloadIcon}
          >
            <div className={styles.btnContent}>
              <span className={styles.btnIcon}>
                <img alt="download icon" src={downloadIcon} />
              </span>
              {t(`${i18nPath.dictionary}.downloadButton`, 'Download Manual')}
            </div>
          </Button>
        </Col>
      </Row>
    </section>
  );
};

export default DocumentsSelector;
